import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider
} from '@mui/material';
import {
  Phone,
  VerifiedUser,
  CheckCircle
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { PhoneNumberInput } from './PhoneNumberInput';
import { SMSVerificationModal } from './SMSVerificationModal';
import { validatePhoneNumber } from '@/utils/phone-validation';

interface PhoneAuthFlowProps {
  mode: 'login' | 'register';
  onSuccess: (phoneNumber: string) => void;
  onError: (error: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

interface PhoneAuthForm {
  phoneNumber: string;
}

const steps = ['Enter Phone Number', 'Verify SMS Code', 'Complete'];

export const PhoneAuthFlow: React.FC<PhoneAuthFlowProps> = ({
  mode,
  onSuccess,
  onError,
  isLoading = false,
  disabled = false
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<PhoneAuthForm>({ mode: 'onChange' });

  const handlePhoneNumberChange = (value: string, isValid: boolean) => {
    setPhoneNumber(value);
    setIsPhoneValid(isValid);
    setError(null);
  };

  const handleSendVerificationCode = async () => {
    if (!isPhoneValid) {
      setError('Please enter a valid phone number');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const validation = validatePhoneNumber(phoneNumber);
      if (!validation.isValid) {
        throw new Error(validation.error || 'Invalid phone number');
      }

      // Here you would typically call your backend to send SMS
      // For now, we'll simulate the API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setActiveStep(1);
      setShowVerificationModal(true);
      setSuccess('Verification code sent successfully!');
    } catch (err: any) {
      console.error('Send verification code error:', err);
      setError(err.message || 'Failed to send verification code');
      onError(err.message || 'Failed to send verification code');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerificationComplete = async (code: string) => {
    try {
      // Here you would verify the code with your backend
      // For now, we'll simulate the verification
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setActiveStep(2);
      setShowVerificationModal(false);
      setSuccess('Phone number verified successfully!');
      
      // Complete the authentication flow
      setTimeout(() => {
        onSuccess(phoneNumber);
      }, 1500);
    } catch (err: any) {
      console.error('Verification error:', err);
      throw err; // Re-throw to let the modal handle it
    }
  };

  const handleResendCode = async () => {
    try {
      // Resend verification code
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Verification code resent');
    } catch (err: any) {
      console.error('Resend code error:', err);
      throw err;
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setPhoneNumber('');
    setIsPhoneValid(false);
    setShowVerificationModal(false);
    setError(null);
    setSuccess(null);
    reset();
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {mode === 'login' ? 'Sign in with Phone Number' : 'Register with Phone Number'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {mode === 'login' 
                ? 'Enter your phone number to receive a verification code'
                : 'Enter your phone number to create your account'
              }
            </Typography>

            <PhoneNumberInput
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              disabled={isSubmitting || disabled}
              required
              autoFocus
              showValidationStatus
            />

            <Button
              fullWidth
              variant="contained"
              onClick={handleSendVerificationCode}
              disabled={!isPhoneValid || isSubmitting || disabled}
              sx={{ mt: 3, py: 1.5 }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Send Verification Code'
              )}
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <VerifiedUser color="primary" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Verification Code Sent
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We've sent a 6-digit code to your phone number.
              The verification modal will open automatically.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setShowVerificationModal(true)}
              disabled={disabled}
            >
              Open Verification Modal
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Phone Number Verified!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {mode === 'login' 
                ? 'You will be signed in shortly...'
                : 'Your account is being created...'
              }
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Progress Stepper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Step Content */}
      <Paper sx={{ p: 3, mb: 2 }}>
        {getStepContent(activeStep)}
      </Paper>

      {/* Reset Button */}
      {activeStep > 0 && activeStep < 2 && (
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="text"
            onClick={handleReset}
            disabled={isSubmitting || disabled}
            color="inherit"
          >
            Start Over
          </Button>
        </Box>
      )}

      {/* SMS Verification Modal */}
      <SMSVerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        phoneNumber={phoneNumber}
        onVerificationComplete={handleVerificationComplete}
        onResendCode={handleResendCode}
        isLoading={isLoading}
        title={`Verify Phone Number - ${mode === 'login' ? 'Sign In' : 'Registration'}`}
        description={mode === 'login' 
          ? 'Enter the verification code to sign in to your account'
          : 'Enter the verification code to complete your registration'
        }
      />
    </Box>
  );
};

export default PhoneAuthFlow;